/* KBN — Engraving (Gravur) Add-On
 * Listens (DOCUMENT-LEVEL CAPTURE) for any /cart/add form submit. If the
 * engraving toggle is ON and the input is non-empty, intercepts the submit
 * and posts a multi-item /cart/add.js adding (1) the main product with a
 * visible "Gravur" property and (2) the gravur add-on with a visible "Für"
 * property + a private _pair_id linking both line items.
 *
 * Why document-level capture (not form-level):
 *   Listeners on the same element fire in registration order during the
 *   target phase — capture-vs-bubble only matters when traversing ancestors.
 *   The theme's <product-form> custom element registers its submit handler
 *   in connectedCallback (early). If we attach to the form afterwards,
 *   ours fires AFTER the theme's. Attaching to `document` with capture: true
 *   fires during the actual capture phase, before the event reaches the form.
 */
(function () {
  if (window.__KBN_ENGRAVING_INIT__) {
    if (window.KBN_ENGRAVING_DEBUG) console.log('[kbn-engraving] already initialized, skipping');
    return;
  }
  window.__KBN_ENGRAVING_INIT__ = true;

  // Verbose logging — set window.KBN_ENGRAVING_DEBUG = false to silence
  // (default ON so we can troubleshoot the initial integration).
  function log() {
    if (window.KBN_ENGRAVING_DEBUG === false) return;
    console.log.apply(console, ['[kbn-engraving]'].concat([].slice.call(arguments)));
  }
  function warn() { console.warn.apply(console, ['[kbn-engraving]'].concat([].slice.call(arguments))); }

  log('script loaded, v2');

  function init() {
    const blocks = document.querySelectorAll('[data-engraving-block]');
    log('init() found', blocks.length, 'engraving block(s)');
    blocks.forEach(setupBlock);
  }

  function setupBlock(block) {
    if (block.dataset.kbnReady === '1') return;
    block.dataset.kbnReady = '1';

    const toggle = block.querySelector('[data-engraving-toggle]');
    const input = block.querySelector('[data-engraving-input]');
    const counter = block.querySelector('[data-engraving-counter]');
    if (!toggle || !input || !counter) {
      warn('setupBlock: missing required elements', { toggle: !!toggle, input: !!input, counter: !!counter });
      return;
    }

    const maxChars = parseInt(input.getAttribute('maxlength'), 10) || 25;
    log('setupBlock ready', { variantId: block.dataset.engravingVariantId, maxChars });

    const errorEl = block.querySelector('[data-engraving-error]');
    const inputBox = input.closest('.kbn-eng-input-box');
    const previewWrap = block.querySelector('[data-engraving-preview-wrap]');
    const previewText = block.querySelector('[data-engraving-preview-text]');

    function updateCounter() {
      counter.textContent = input.value.length + '/' + maxChars;
    }
    function hideError() {
      if (errorEl) errorEl.hidden = true;
      if (inputBox) inputBox.classList.remove('is-error');
    }
    function updatePreview() {
      if (!previewWrap || !previewText) return;
      const val = input.value;
      if (val.length > 0) {
        previewText.textContent = val;
        previewWrap.classList.add('is-shown');
      } else {
        previewText.textContent = '';
        previewWrap.classList.remove('is-shown');
      }
    }

    input.addEventListener('input', () => {
      updateCounter();
      // As soon as the user types, dismiss any previously shown error
      hideError();
      updatePreview();
    });
    updateCounter();
    updatePreview();

    toggle.addEventListener('change', () => {
      const on = toggle.checked;
      block.classList.toggle('is-on', on);
      log('toggle changed → checked=' + on);
      hideError(); // toggling clears any stale error
      if (on) {
        setTimeout(() => input.focus(), 80);
      } else {
        input.value = '';
        updateCounter();
        updatePreview();
      }
    });

    // Hinweis-Akkordeon: animated open/close via .is-open class
    const hintWrap = block.querySelector('[data-engraving-hint]');
    const hintToggle = block.querySelector('[data-engraving-hint-toggle]');
    if (hintWrap && hintToggle) {
      hintToggle.addEventListener('click', () => {
        const isOpen = hintWrap.classList.toggle('is-open');
        hintToggle.setAttribute('aria-expanded', String(isOpen));
      });
    }
  }

  // Document-level capture listener — fires before any form-level listener
  document.addEventListener('submit', async function (e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    log('submit captured on form action=' + form.action);

    // Find an engraving block that belongs to this form's section
    const block = findBlockForForm(form);
    if (!block) {
      log('no engraving block found for this form — passing through');
      return;
    }

    const toggle = block.querySelector('[data-engraving-toggle]');
    if (!toggle || !toggle.checked) {
      log('engraving toggle is OFF — passing through');
      return;
    }

    const input = block.querySelector('[data-engraving-input]');
    const text = input.value.trim();
    if (!text) {
      log('toggle ON but input empty → blocking submit + showing error');
      e.preventDefault();
      e.stopImmediatePropagation();

      // Visual feedback: red border + show error message
      const errorEl = block.querySelector('[data-engraving-error]');
      const inputBox = input.closest('.kbn-eng-input-box');
      if (errorEl) errorEl.hidden = false;
      if (inputBox) inputBox.classList.add('is-error');

      input.focus();
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    log('intercepting submit — will add main + gravur items');

    const variantMapEl = block.querySelector('[data-engraving-variant-map]');
    let variantMap = {};
    try { variantMap = variantMapEl ? JSON.parse(variantMapEl.textContent) : {}; } catch (err) {}

    const gravurVariantId = block.dataset.engravingVariantId;
    const productTitle = block.dataset.engravingProductTitle || '';
    const labelKey = block.dataset.engravingLabelKey || 'Gravur';
    const forKey = block.dataset.engravingForKey || 'Für';
    const contentKey = block.dataset.engravingContentKey || 'Inhalt';

      if (!gravurVariantId) {
        console.warn('[kbn-engraving] No gravur variant id found, falling back to normal submit.');
        form.requestSubmit ? form.requestSubmit() : form.submit();
        return;
      }

      const formData = new FormData(form);
      const mainVariantId = formData.get('id');
      const qty = parseInt(formData.get('quantity') || '1', 10) || 1;

      // Variant title for the "Für: ..." display
      const variantTitle = variantMap[mainVariantId] || '';
      let productLabel = productTitle;
      if (variantTitle && variantTitle !== 'Default Title') {
        productLabel += ' – ' + variantTitle;
      }

      // Unique pair id linking both line items
      const pairId =
        'kbn-' +
        Math.random().toString(36).slice(2, 8) +
        '-' +
        Date.now().toString(36);

      // Build properties — capture any other line-item-property inputs already
      // in the form (e.g. theme's `line_item_property` block) so we don't
      // lose them when bypassing the theme's single-item submit.
      const mainProps = collectFormProperties(formData);
      mainProps[labelKey] = text;
      mainProps['_pair_id'] = pairId;
      mainProps['_kbn_role'] = 'main';
      // Store the key we used for the visible engraving label so the sync
      // script knows which property to strip when the addon is removed later.
      mainProps['_engraving_property_key'] = labelKey;

      const items = [
        {
          id: parseInt(mainVariantId, 10),
          quantity: qty,
          properties: mainProps
        },
        {
          id: parseInt(gravurVariantId, 10),
          quantity: qty,
          properties: {
            [contentKey]: text,
            [forKey]: productLabel,
            '_pair_id': pairId,
            '_kbn_role': 'addon'
          }
        }
      ];

      // Inform theme about loading
      const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
      submitButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      // Let other sections bundle their sections for theme's cart refresh
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      try {
        const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: `${Shopify.routes.root}variants/${mainVariantId}`
          })
        });
        const addJson = await addResponse.json();

        if (!addResponse.ok) {
          form.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: addJson.description || addJson.message || 'Add failed' }
            })
          );
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = addJson.sections;

        // Same events the native <product-form> would fire
        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: addJson.items || (Array.isArray(addJson) ? addJson : [addJson]),
              cart: cartContent,
              onSuccessDo: formData.get('on_success')
            }
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: formData.get('on_success'),
              cart: cartContent
            }
          })
        );

        // Reset the UI for next add
        toggle.checked = false;
        input.value = '';
        block.classList.remove('is-on');
        const counter = block.querySelector('[data-engraving-counter]');
        if (counter) counter.textContent = '0/' + (parseInt(input.getAttribute('maxlength'), 10) || 25);
        const previewWrap = block.querySelector('[data-engraving-preview-wrap]');
        const previewText = block.querySelector('[data-engraving-preview-text]');
        if (previewWrap) previewWrap.classList.remove('is-shown');
        if (previewText) previewText.textContent = '';
        log('multi-item add succeeded');
      } catch (err) {
        console.error('[kbn-engraving] add failed', err);
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: 'Network error' }
          })
        );
      } finally {
        submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
  }, { capture: true });

  // Find an engraving block belonging to the same section / form scope as the
  // submitted form. Strategy (in order):
  //   1. Look inside the form itself (rare, but possible)
  //   2. Walk up to the closest <product-form> custom element and search inside
  //   3. Walk up to the closest [id^="shopify-section-"] and search inside
  //   4. Fall back to the first engraving block on the page
  function findBlockForForm(form) {
    let block = form.querySelector('[data-engraving-block]');
    if (block) return block;

    const pf = form.closest('product-form');
    if (pf) {
      block = pf.querySelector('[data-engraving-block]');
      if (block) return block;
    }

    let el = form.parentElement;
    while (el && el !== document.body) {
      if (el.id && el.id.indexOf('shopify-section-') === 0) {
        block = el.querySelector('[data-engraving-block]');
        if (block) return block;
        break;
      }
      el = el.parentElement;
    }

    return document.querySelector('[data-engraving-block]') || null;
  }

  function collectFormProperties(formData) {
    const props = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) props[match[1]] = value;
    }
    return props;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', init);
})();
